import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterField, FilterOption } from '@/components/common/AdvancedFilter';
import { useDebounce } from '@/hooks/useDebounce';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import { specimensQueryOptions } from '../api/product.api';
import { DEFAULT_PRODUCT_PAGE_SIZE, SPECIMEN_PICKER_LIMIT } from '../constants/product.constants';
import type { Specimen } from '../types/product.types';

const MIN_SEARCH_LENGTH = 2;

function toSpecimenOptions(specimens: Specimen[] | undefined): FilterOption[] {
  return (specimens ?? []).slice(0, SPECIMEN_PICKER_LIMIT).map((specimen) => ({
    value: String(specimen.id),
    label: specimen.name,
    description: [specimen.spCode, specimen.barCode].filter(Boolean).join(OPTION_DETAIL_SEPARATOR),
  }));
}

export function useSpecimenFilterField(selectedId: string): FilterField {
  const [query, setQuery] = useState('');
  const searchText = useDebounce(query.trim());
  const canSearch = searchText.length >= MIN_SEARCH_LENGTH;

  const { data, isFetching } = useQuery({
    ...specimensQueryOptions({ search: searchText, page: 1, pageSize: DEFAULT_PRODUCT_PAGE_SIZE }),
    enabled: canSearch,
  });

  const results = canSearch ? toSpecimenOptions(data?.data) : [];
  const isSelectedInResults =
    selectedId === '' || results.some((option) => option.value === selectedId);
  const options = isSelectedInResults
    ? results
    : [{ value: selectedId, label: `Specimen #${selectedId}` }, ...results];

  return {
    key: 'specimenId',
    label: 'Specimen',
    type: 'select',
    placeholder: 'All Specimens',
    options,
    onSearchChange: setQuery,
    isSearching: canSearch && isFetching,
  };
}
